/**
 * Loan detail — one loan's complete story: the schedule, the money in and
 * out, and the ledger behind it.
 *
 * The schedule is the source of truth for what is owed; `outstanding_balance`
 * is a derived convenience field. When the two disagree the page says so
 * loudly rather than quietly showing a number an operator would defend to a
 * customer, so a disputed balance can be traced instead of argued.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Phone } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Row = Record<string, any>;

const tone: Record<string, string> = {
  active: "bg-success/10 text-success",
  approved: "bg-success/10 text-success",
  paid: "bg-success/10 text-success",
  succeeded: "bg-success/10 text-success",
  kept: "bg-success/10 text-success",
  paid_off: "bg-muted text-muted-foreground",
  closed: "bg-muted text-muted-foreground",
  pending: "bg-warning/10 text-warning",
  partial: "bg-warning/10 text-warning",
  late: "bg-warning/10 text-warning",
  overdue: "bg-warning/10 text-warning",
  submitted: "bg-warning/10 text-warning",
  processing: "bg-warning/10 text-warning",
  pending_customer: "bg-warning/10 text-warning",
  open: "bg-warning/10 text-warning",
  failed: "bg-destructive/10 text-destructive",
  expired: "bg-destructive/10 text-destructive",
  broken: "bg-destructive/10 text-destructive",
  defaulted: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
};
const badge = (v?: string | null) => (
  <Badge className={tone[v || ""] || "bg-muted text-muted-foreground"}>
    {String(v || "—").split("_").join(" ")}
  </Badge>
);

const asDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : "—");
const asMoment = (v?: string | null) => (v ? new Date(v).toLocaleString() : "—");

/** Overdue only counts once the due date has passed and money is still owed. */
const isOverdue = (i: Row) =>
  i.status !== "paid" && i.due_date && new Date(i.due_date) < new Date();

function Stat({ label, value, hint, alert }: {
  label: string; value: string; hint?: string; alert?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold tabular-nums ${alert ? "text-destructive" : ""}`}>
        {value}
      </p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function LoanDetailPage() {
  const { loanId } = useParams();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loanId) return;
    staffApi.loanDetail(loanId)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load the loan"))
      .finally(() => setLoading(false));
  }, [loanId]);

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;
  if (error || !data) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-destructive">{error || "Not found"}</p>
        <Button variant="link" asChild className="mt-2">
          <Link to="/staff/loans">Back to Loans</Link>
        </Button>
      </div>
    );
  }

  const l = data.loan as Row;
  const borrower = data.borrower as Row | null;
  const app = data.application as Row | null;
  const collection = data.collection as Row | null;
  const installments: Row[] = data.installments || [];
  const repaidPct = l.total > 0 ? Math.min(100, (l.paid_to_date / l.total) * 100) : 0;
  const arrears = installments
    .filter(isOverdue)
    .reduce((sum, i) => sum + i.outstanding, 0);

  // Ledger rows arrive newest-first; grouping by posting batch keeps each
  // balanced set of debits and credits together the way it was written.
  const ledgerGroups: [string, Row[]][] = [];
  for (const row of (data.ledger || []) as Row[]) {
    const last = ledgerGroups[ledgerGroups.length - 1];
    if (last && last[0] === row.group) last[1].push(row);
    else ledgerGroups.push([row.group, [row]]);
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link to="/staff/loans"><ArrowLeft className="mr-1 h-4 w-4" />Loans</Link>
      </Button>

      {/* The balance is derived from the schedule — drift must never be silent. */}
      {l.balance_matches_schedule === false && (
        <Card className="border-destructive">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div className="text-sm">
              <p className="font-medium text-destructive">
                The recorded balance does not match the schedule
              </p>
              <p className="mt-1 text-muted-foreground">
                Stored balance is <strong>{formatCurrency(l.outstanding)}</strong>, but the
                instalments add up to <strong>{formatCurrency(l.expected_outstanding)}</strong> —
                a gap of {formatCurrency(Math.abs(l.outstanding - l.expected_outstanding))}. Treat
                the schedule as correct and check the ledger below before quoting a figure to the
                customer.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Identity */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">Loan {l.ref}</h1>
                {badge(l.status)}
              </div>
              {borrower ? (
                <>
                  <Link to={`/staff/clients/${borrower.id}`}
                    className="mt-1 inline-block font-medium text-primary hover:underline">
                    {borrower.name || "Unnamed customer"}
                  </Link>
                  <a href={`tel:${borrower.phone}`}
                    className="mt-0.5 flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />{borrower.phone}
                  </a>
                </>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">Borrower record missing</p>
              )}
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <p>Disbursed {asDate(l.disbursed_on)}</p>
              <p>Final due {asDate(l.due_date)}</p>
            </div>
          </div>

          <div className="mt-5 border-t pt-4">
            <div className="flex items-end justify-between">
              <p className="text-xs text-muted-foreground">
                Repaid {formatCurrency(l.paid_to_date)} of {formatCurrency(l.total)}
              </p>
              <p className="text-xs font-medium tabular-nums">{repaidPct.toFixed(0)}%</p>
            </div>
            <Progress value={repaidPct} className="mt-1.5 h-2" />
          </div>

          <div className="mt-5 grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Outstanding" value={formatCurrency(l.outstanding)}
              hint={l.balance_matches_schedule === false
                ? `schedule says ${formatCurrency(l.expected_outstanding)}`
                : "matches the schedule"}
              alert={l.balance_matches_schedule === false} />
            <Stat label="Principal" value={formatCurrency(l.principal)}
              hint={`${l.interest_rate}% · ${l.term_months} months`} />
            <Stat label="Monthly instalment" value={formatCurrency(l.monthly_payment)} />
            {l.next_due ? (
              <Stat label="Next due" value={formatCurrency(l.next_due.amount)}
                hint={`#${l.next_due.sequence} on ${asDate(l.next_due.date)}`} />
            ) : (
              <Stat label="Next due" value="—" hint="nothing outstanding" />
            )}
          </div>

          {arrears > 0 && (
            <p className="mt-3 text-sm text-destructive">
              {formatCurrency(arrears)} is past due across{" "}
              {installments.filter(isOverdue).length} instalment
              {installments.filter(isOverdue).length === 1 ? "" : "s"}.
            </p>
          )}

          {collection && (
            <p className="mt-2 text-sm text-warning">
              In collections — {collection.days_past_due} days past due (bucket{" "}
              {collection.bucket}), {formatCurrency(collection.amount_overdue)} overdue.{" "}
              <Link to="/staff/collections" className="underline">Open Collections</Link>
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="schedule">
        <TabsList>
          <TabsTrigger value="schedule">Schedule ({installments.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({data.payments.length})</TabsTrigger>
          <TabsTrigger value="provider">Provider ({data.intents.length})</TabsTrigger>
          <TabsTrigger value="ledger">Ledger ({data.ledger.length})</TabsTrigger>
          <TabsTrigger value="contact">Contact ({data.interactions.length})</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule" className="pt-4">
          <Card><CardContent className="pt-4">
            {installments.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No schedule has been generated for this loan.
              </p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>#</TableHead><TableHead>Due</TableHead>
                  <TableHead className="text-right">Due amount</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead><TableHead>Settled</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {installments.map((i) => (
                    <TableRow key={i.sequence}
                      className={isOverdue(i) ? "bg-destructive/5" : undefined}>
                      <TableCell className="text-sm">{i.sequence}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {asDate(i.due_date)}
                        {isOverdue(i) && (
                          <span className="ml-2 text-xs text-destructive">past due</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(i.amount_due)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(i.amount_paid)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(i.outstanding)}</TableCell>
                      <TableCell>{badge(i.status)}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {i.paid_at ? asMoment(i.paid_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payments" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.payments.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nothing has been repaid on this loan yet.
              </p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead><TableHead>Reference</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.payments.map((p: Row) => (
                    <TableRow key={p.id}>
                      <TableCell className="whitespace-nowrap text-sm">{asDate(p.date)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(p.amount)}</TableCell>
                      <TableCell className="text-sm capitalize">
                        {String(p.method || "—").split("_").join(" ")}</TableCell>
                      <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="provider" className="pt-4">
          <Card><CardContent className="pt-4">
            <p className="pb-3 text-xs text-muted-foreground">
              Every attempt to move money for this loan, including the ones that failed.
            </p>
            {data.intents.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No provider attempts recorded.
              </p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Started</TableHead><TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Reference</TableHead><TableHead>Operator</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.intents.map((i: Row) => (
                    <TableRow key={i.id}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {asMoment(i.created_at)}</TableCell>
                      <TableCell className="text-sm capitalize">{i.kind}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(i.amount)}</TableCell>
                      <TableCell className="font-mono text-xs">{i.reference}</TableCell>
                      <TableCell className="text-sm capitalize">{i.operator || "—"}</TableCell>
                      <TableCell>
                        {badge(i.status)}
                        {i.reason && (
                          <p className="mt-1 max-w-[18rem] text-xs text-destructive">{i.reason}</p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ledger" className="pt-4">
          <Card><CardContent className="pt-4">
            <p className="pb-3 text-xs text-muted-foreground">
              The double-entry trail. Each block below was posted together and balances —
              this is what a disputed balance should be traced against.
            </p>
            {ledgerGroups.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nothing has been posted to the ledger for this loan.
              </p>
            ) : (
              <div className="space-y-4">
                {ledgerGroups.map(([group, rows]) => {
                  const debits = rows.reduce((s, r) => s + (r.debit || 0), 0);
                  const credits = rows.reduce((s, r) => s + (r.credit || 0), 0);
                  const balanced = Math.abs(debits - credits) < 0.01;
                  return (
                    <div key={group} className="rounded-md border">
                      <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2">
                        <span className="text-xs text-muted-foreground">
                          {asMoment(rows[0].posted_at)}
                        </span>
                        <span className={`text-xs ${balanced ? "text-muted-foreground" : "text-destructive"}`}>
                          {balanced
                            ? `balanced · ${formatCurrency(debits)}`
                            : `unbalanced · Dr ${formatCurrency(debits)} vs Cr ${formatCurrency(credits)}`}
                        </span>
                      </div>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Account</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                          <TableHead>Memo</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {rows.map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm capitalize">
                                {String(r.account).split("_").join(" ")}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {r.debit ? formatCurrency(r.debit) : "—"}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {r.credit ? formatCurrency(r.credit) : "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {r.memo || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="contact" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.interactions.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No contact logged against this loan.
              </p>
            ) : (
              <ol className="space-y-3 border-l pl-4">
                {data.interactions.map((i: Row, idx: number) => (
                  <li key={idx} className="relative text-sm">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                    <p className="text-xs text-muted-foreground">{asMoment(i.at)}</p>
                    <p className="font-medium">
                      {String(i.channel).split("_").join(" ")} —{" "}
                      {String(i.outcome).split("_").join(" ")}
                    </p>
                    {i.notes && <p className="text-muted-foreground">{i.notes}</p>}
                  </li>
                ))}
              </ol>
            )}
            {data.promises.length > 0 && (
              <div className="mt-5 border-t pt-4">
                <p className="mb-2 text-sm font-medium">Promises to pay</p>
                {data.promises.map((p: Row, i: number) => (
                  <div key={i} className="flex items-center justify-between py-1 text-sm">
                    <span>{formatCurrency(p.amount)} by {asDate(p.date)}</span>
                    {badge(p.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4 pt-4">
          {app && (
            <Card><CardContent className="pt-5">
              <p className="mb-3 text-sm font-medium">Where this loan came from</p>
              <div className="grid gap-4 text-sm sm:grid-cols-4">
                <Stat label="Requested" value={formatCurrency(app.amount)} />
                <div>
                  <p className="text-xs text-muted-foreground">Purpose</p>
                  <p className="mt-0.5">{app.purpose || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Applied</p>
                  <p className="mt-0.5">{asDate(app.applied_on)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Approved</p>
                  <p className="mt-0.5">{asDate(app.approved_on)}</p>
                </div>
              </div>
            </CardContent></Card>
          )}
          <Card><CardContent className="pt-4">
            {data.activity.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No recorded activity.
              </p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>By</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.activity.map((e: Row, i: number) => (
                    <TableRow key={i}>
                      <TableCell className="whitespace-nowrap text-xs">{asMoment(e.at)}</TableCell>
                      <TableCell className="text-sm">
                        {String(e.action).split("_").join(" ")}</TableCell>
                      <TableCell className="text-xs capitalize text-muted-foreground">
                        {String(e.actor_role || "system").split("_").join(" ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
